import React, { useMemo } from "react";
import Dropdown from "./Dropdown";
import { SmallTrashIcon } from "../icons";
import S from "../styles";

export default function LineItemCard({ item, index, materials, onChange, onRemove, canRemove }) {
  const types = useMemo(() => [...new Set(materials.map((m) => m.type))].sort(), [materials]);

  const colors = useMemo(
    () => (item.type ? [...new Set(materials.filter((m) => m.type === item.type).map((m) => m.color))].sort() : []),
    [materials, item.type]
  );

  const sizes = useMemo(
    () =>
      item.type && item.color
        ? [...new Set(materials.filter((m) => m.type === item.type && m.color === item.color).map((m) => m.sheetSize))].sort()
        : [],
    [materials, item.type, item.color]
  );

  const thicknesses = useMemo(
    () =>
      item.type && item.color && item.sheetSize
        ? [
            ...new Set(
              materials
                .filter((m) => m.type === item.type && m.color === item.color && m.sheetSize === item.sheetSize)
                .map((m) => m.thickness)
            ),
          ].sort()
        : [],
    [materials, item.type, item.color, item.sheetSize]
  );

  const matched = useMemo(
    () =>
      materials.find(
        (m) =>
          m.type === item.type &&
          m.color === item.color &&
          m.sheetSize === item.sheetSize &&
          m.thickness === item.thickness
      ),
    [materials, item.type, item.color, item.sheetSize, item.thickness]
  );

  const update = (field, value) => {
    const next = { ...item, [field]: value };
    if (field === "type") { next.color = ""; next.sheetSize = ""; next.thickness = ""; }
    if (field === "color") { next.sheetSize = ""; next.thickness = ""; }
    if (field === "sheetSize") { next.thickness = ""; }
    onChange(next);
  };

  const sheetsNeeded =
    item.partsPerSheet > 0 && item.partsNeeded > 0 ? Math.ceil(item.partsNeeded / item.partsPerSheet) : 0;
  const lineCost = matched ? sheetsNeeded * matched.price : 0;

  return (
    <div style={S.lineCard}>
      <div style={S.lineCardHeader}>
        <span style={S.lineCardNumber}>Item {index + 1}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {matched && lineCost > 0 && <span style={S.lineCardCost}>${lineCost.toFixed(2)}</span>}
          {canRemove && (
            <button onClick={onRemove} style={S.removeLineBtn} title="Remove item">
              <SmallTrashIcon />
            </button>
          )}
        </div>
      </div>
      <div style={S.lineCardBody}>
        <div className="selection-grid" style={S.selectionGrid}>
          <Dropdown label="Material Type" value={item.type} onChange={(v) => update("type", v)} options={types.map((t) => ({ value: t, label: t }))} placeholder="Choose material..." />
          <Dropdown label="Color / Variant" value={item.color} onChange={(v) => update("color", v)} options={colors.map((c) => ({ value: c, label: c }))} placeholder="Choose color..." disabled={!item.type} />
          <Dropdown label="Sheet Size" value={item.sheetSize} onChange={(v) => update("sheetSize", v)} options={sizes.map((s) => ({ value: s, label: s }))} placeholder="Choose size..." disabled={!item.color} />
          <Dropdown label="Thickness" value={item.thickness} onChange={(v) => update("thickness", v)} options={thicknesses.map((t) => ({ value: t, label: t }))} placeholder="Choose thickness..." disabled={!item.sheetSize} />
        </div>
        {matched && (
          <div style={S.priceTag}>
            <span style={{ color: "rgba(10,10,15,0.58)", fontSize: 13, textTransform: "uppercase", letterSpacing: 1.5 }}>Sheet Price</span>
            <span style={{ fontSize: 24, fontWeight: 700, color: "#0033FF", fontFamily: "'JetBrains Mono', monospace" }}>
              ${matched.price.toFixed(2)}
            </span>
          </div>
        )}
        <div style={S.paramsRow}>
          <div style={{ flex: 1 }}>
            <label style={S.label}>Parts Per Sheet</label>
            <input type="number" min="1" value={item.partsPerSheet} onChange={(e) => update("partsPerSheet", Math.max(0, parseInt(e.target.value) || 0))} placeholder="e.g. 6" style={S.input} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={S.label}>Total Parts Needed</label>
            <input type="number" min="1" value={item.partsNeeded} onChange={(e) => update("partsNeeded", Math.max(0, parseInt(e.target.value) || 0))} placeholder="e.g. 220" style={S.input} />
          </div>
        </div>
        {matched && sheetsNeeded > 0 && (
          <div style={S.lineResultBar}>
            <div style={S.lineResultItem}>
              <span style={{ color: "rgba(10,10,15,0.58)", fontSize: 13, textTransform: "uppercase", letterSpacing: 1 }}>Sheets</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 18, color: "#0A0A0F" }}>{sheetsNeeded}</span>
            </div>
            <div style={{ width: 1, height: 28, background: "#F4F2EC" }} />
            <div style={S.lineResultItem}>
              <span style={{ color: "rgba(10,10,15,0.58)", fontSize: 13, textTransform: "uppercase", letterSpacing: 1 }}>Cost/Part</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 18, color: "#0A0A0F" }}>
                ${(lineCost / item.partsNeeded).toFixed(2)}
              </span>
            </div>
            <div style={{ width: 1, height: 28, background: "#F4F2EC" }} />
            <div style={S.lineResultItem}>
              <span style={{ color: "rgba(10,10,15,0.58)", fontSize: 13, textTransform: "uppercase", letterSpacing: 1 }}>Line Total</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 18, color: "#0033FF" }}>
                ${lineCost.toFixed(2)}
              </span>
            </div>
            {item.partsNeeded % item.partsPerSheet !== 0 && (
              <>
                <div style={{ width: 1, height: 28, background: "#F4F2EC" }} />
                <div style={S.lineResultItem}>
                  <span style={{ color: "rgba(10,10,15,0.58)", fontSize: 13, textTransform: "uppercase", letterSpacing: 1 }}>Last Sheet</span>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 15, color: "rgba(10,10,15,0.58)" }}>
                    {item.partsNeeded % item.partsPerSheet}/{item.partsPerSheet} used
                  </span>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
